import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './hooks/useAuth';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import HomePage from './pages/HomePage';
import AuthScreen from './pages/AuthScreen';
import Dashboard from './pages/Dashboard';
import Pricing from './pages/Pricing';
import ReportForm from './pages/ReportForm';
import ReportsList from './pages/ReportsList';
import ReportView from './pages/ReportView';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import FAQ from './pages/FAQ';
import Contact from './pages/Contact';
import Verify from './pages/Verify';
import DataRequest from './pages/DataRequest';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminReports from './pages/AdminReports';
import AdminUsers from './pages/AdminUsers';
import AdminConfig from './pages/AdminConfig';
import AdminVouchers from './pages/AdminVouchers';
import AdminReviews from './pages/AdminReviews';
import AdminAudit from './pages/AdminAudit';
import AdminApiKeys from './pages/AdminApiKeys';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children;
};

// Admin Route wrapper
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || !isAdmin) {
    return <Navigate to="/admin-portal-7x9k2m" replace />;
  }
  
  return children;
};

// ─────────────────────────────────────────────────────────────────────────────
// Cookie Consent — categorized (Essential / Analytics / Marketing)
//
// Compliance design:
// - Essential is required and cannot be opted out of (auth token, etc.)
// - Analytics + Marketing are opt-in only, default OFF, granular toggles
// - "Cookie preferences" link in the footer re-opens the banner so withdrawing
//   consent is as easy as giving it (UK PECR / GDPR Art 7(3))
// - consentVersion is bumped whenever the categories change, forcing a re-prompt
// - Honest copy: "We store a login token in your browser" rather than the old
//   "no cookies are used" line that conflicted with localStorage auth
//
// Server-side persistence (POST /api/consent) is wired up server-side; this
// component fires it best-effort but does not block UX on its result.
// ─────────────────────────────────────────────────────────────────────────────
const CONSENT_VERSION = 2;
const CONSENT_KEY = 'cookieConsent.v2';

// Listen for a global event to re-open the banner (footer "Cookie preferences" link)
const REOPEN_EVENT = 'id100:open-cookie-prefs';

const CookieConsent = () => {
  const [shown, setShown] = React.useState(false);
  const [analytics, setAnalytics] = React.useState(false);
  const [marketing, setMarketing] = React.useState(false);

  React.useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      setShown(true);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      if (!parsed.version || parsed.version < CONSENT_VERSION) {
        setShown(true); // re-prompt on version bump
      } else {
        setAnalytics(!!parsed.analytics);
        setMarketing(!!parsed.marketing);
      }
    } catch (_) {
      setShown(true);
    }
    const onReopen = () => setShown(true);
    window.addEventListener(REOPEN_EVENT, onReopen);
    return () => window.removeEventListener(REOPEN_EVENT, onReopen);
  }, []);

  const persist = (choice) => {
    const record = {
      version: CONSENT_VERSION,
      essential: true,
      analytics: choice.analytics,
      marketing: choice.marketing,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
    // Best-effort server log (anonymous OK). Don't block UX on failure.
    try {
      const apiBase = process.env.REACT_APP_API_URL || '/api';
      fetch(`${apiBase}/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(record)
      }).catch(() => { /* non-fatal */ });
    } catch (_) { /* non-fatal */ }
    setShown(false);
  };

  if (!shown) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 z-50 shadow-lg" role="dialog" aria-label="Cookie and storage preferences">
      <div className="max-w-4xl mx-auto">
        <p className="text-base font-semibold mb-1">Your privacy choices</p>
        <p className="text-xs text-gray-300 mb-4">
          We store a login token in your browser (not a third-party cookie) so you stay signed in.
          That's classed as essential and is always on. We don't currently load any analytics or
          marketing trackers, but if we do in future the toggles below let you opt in or out.
          See our{' '}
          <a href="/privacy" className="underline text-red-400 hover:text-red-300">Privacy Policy</a> and{' '}
          <a href="/cookies" className="underline text-red-400 hover:text-red-300">Cookie Policy</a> for detail.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4">
          <div className="bg-gray-800 rounded p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Essential</span>
              <span className="text-xs px-2 py-0.5 bg-gray-700 rounded text-gray-300">Always on</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">Login session, security, fraud prevention.</p>
          </div>
          <label className="bg-gray-800 rounded p-3 cursor-pointer">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Analytics</span>
              <input type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="h-4 w-4" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Privacy-friendly usage stats. None loaded today.</p>
          </label>
          <label className="bg-gray-800 rounded p-3 cursor-pointer">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Marketing</span>
              <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="h-4 w-4" />
            </div>
            <p className="text-xs text-gray-400 mt-1">Personalised ads / pixels. None loaded today.</p>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={() => persist({ analytics: false, marketing: false })}
            className="bg-gray-700 hover:bg-gray-600 text-white px-5 py-2 rounded-md text-sm font-medium"
          >
            Reject non-essential
          </button>
          <button
            type="button"
            onClick={() => persist({ analytics, marketing })}
            className="bg-gray-600 hover:bg-gray-500 text-white px-5 py-2 rounded-md text-sm font-medium"
          >
            Save selection
          </button>
          <button
            type="button"
            onClick={() => persist({ analytics: true, marketing: true })}
            className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-md text-sm font-medium"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Pending-deletion banner — shown on top of every protected page when a logged-in
// user has a pending deletion request, with a one-click cancel.
// ─────────────────────────────────────────────────────────────────────────────
const PendingDeletionBanner = () => {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [cancelling, setCancelling] = React.useState(false);

  if (!isAuthenticated || !user?.deletionRequested) return null;

  const requested = new Date(user.deletionRequested);
  const scheduled = new Date(requested.getTime() + 7 * 24 * 60 * 60 * 1000);
  const fmt = scheduled.toLocaleString('en-GB', { dateStyle: 'long', timeZone: 'Europe/London' });

  const cancel = async () => {
    setCancelling(true);
    try {
      const { user: userApi } = await import('./utils/api');
      await userApi.cancelDeletion();
      if (refreshUser) await refreshUser();
    } catch (_) {
      // Surface error inline only — banner stays visible if it failed
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-3 text-sm">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 flex-wrap">
        <span>
          ⚠ Your account is scheduled for deletion on <strong>{fmt}</strong>. After this date your account
          and all reports will be permanently removed.
        </span>
        <button
          onClick={cancel}
          disabled={cancelling}
          className="bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white px-4 py-1.5 rounded text-sm font-medium"
        >
          {cancelling ? 'Cancelling…' : 'Cancel deletion'}
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <PendingDeletionBanner />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/auth" element={<AuthScreen />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/data-request" element={<DataRequest />} />

          {/* Protected user routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute><Dashboard /></ProtectedRoute>
          } />
          <Route path="/reports/new" element={
            <ProtectedRoute><ReportForm /></ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute><ReportsList /></ProtectedRoute>
          } />
          <Route path="/reports/:id" element={
            <ProtectedRoute><ReportView /></ProtectedRoute>
          } />
          
          {/* Admin routes (hidden URL) */}
          <Route path="/admin-portal-7x9k2m" element={<AdminLogin />} />
          <Route path="/admin-portal-7x9k2m/dashboard" element={
            <AdminRoute><AdminDashboard /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/reports" element={
            <AdminRoute><AdminReports /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/users" element={
            <AdminRoute><AdminUsers /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/config" element={
            <AdminRoute><AdminConfig /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/vouchers" element={
            <AdminRoute><AdminVouchers /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/reviews" element={
            <AdminRoute><AdminReviews /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/audit" element={
            <AdminRoute><AdminAudit /></AdminRoute>
          } />
          <Route path="/admin-portal-7x9k2m/api-keys" element={
            <AdminRoute><AdminApiKeys /></AdminRoute>
          } />
          
          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
